import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { mjBizAppsCommonPersonEntity } from '@mj-biz-apps/common-entities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

interface ContactDisplay {
  contact: mjBizAppsCommonPersonEntity;
  isEditing: boolean;
  editForm: ContactEditForm;
}

interface ContactEditForm {
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  photoURL: string;
  // Post-BAC: Person.Status is 'Active' | 'Inactive' | 'Deceased'. We surface only the two
  // operational values in Izzy's UI; "Pending"/"Suspended" no longer exist as Person states
  // (pending memberships live in BC: Invitations; org-level suspension lives on Relationship.Status).
  status: 'Active' | 'Inactive';
}

@Component({
  standalone: false,
  selector: 'izzy-contacts',
  templateUrl: './contacts.component.html',
  styleUrls: ['./contacts.component.css']
})
export class ContactsComponent implements OnInit, OnDestroy, IAdminPanel {
  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() interaction = new EventEmitter<any>();

  constructor() {}

  protected contacts: mjBizAppsCommonPersonEntity[] = [];
  protected contactDisplays: ContactDisplay[] = [];
  protected selectedContact: mjBizAppsCommonPersonEntity | null = null;

  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';
  protected statusFilter: string = 'all';

  protected isCreating: boolean = false;
  protected createForm: ContactEditForm = this.getEmptyForm();

  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getEmptyForm(): ContactEditForm {
    return {
      firstName: '',
      lastName: '',
      email: '',
      title: '',
      photoURL: '',
      status: 'Active'
    };
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const rv = new RunView();
      const result = await rv.RunView<mjBizAppsCommonPersonEntity>({
        EntityName: 'MJ_BizApps_Common: People',
        OrderBy: 'LastName ASC, FirstName ASC',
        ResultType: 'entity_object'
      });
      this.contacts = result.Results;
      this.contactDisplays = this.contacts.map(c => ({
        contact: c,
        isEditing: false,
        editForm: this.contactToForm(c)
      }));

      // Emit default status (contacts don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading contacts:', error);
      this.errorMessage = 'Failed to load contacts.';
    } finally {
      this.isLoading = false;
    }
  }

  private contactToForm(contact: mjBizAppsCommonPersonEntity): ContactEditForm {
    // Person.Status can be 'Deceased'; for UI editing we collapse it to 'Inactive' so the
    // dropdown stays simple. Saving from the form will only ever write 'Active' or 'Inactive'.
    const uiStatus: 'Active' | 'Inactive' =
      contact.Status === 'Active' ? 'Active' : 'Inactive';
    return {
      firstName: contact.FirstName,
      lastName: contact.LastName,
      email: contact.Email,
      title: contact.Title || '',
      photoURL: contact.PhotoURL || '',
      status: uiStatus
    };
  }

  get filteredContacts(): ContactDisplay[] {
    return this.contactDisplays.filter(cd => {
      const matchesSearch = !this.searchQuery.trim() ||
        cd.contact.FirstName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        cd.contact.LastName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        cd.contact.Email.toLowerCase().includes(this.searchQuery.toLowerCase());
      const matchesStatus = this.statusFilter === 'all' || cd.contact.Status === this.statusFilter;
      return matchesSearch && matchesStatus;
    });
  }

  selectContact(contact: mjBizAppsCommonPersonEntity): void {
    this.selectedContact = contact;
  }

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
  }

  async saveCreate(): Promise<void> {
    if (!this.createForm.firstName.trim() || !this.createForm.lastName.trim() || !this.createForm.email.trim()) {
      this.errorMessage = 'First name, last name, and email are required.';
      return;
    }

    this.isSaving = true;
    try {
      const md = new Metadata();
      const newContact = await md.GetEntityObject<mjBizAppsCommonPersonEntity>('MJ_BizApps_Common: People');
      newContact.FirstName = this.createForm.firstName.trim();
      newContact.LastName = this.createForm.lastName.trim();
      newContact.Email = this.createForm.email.trim();
      newContact.Title = this.createForm.title.trim() || null;
      newContact.PhotoURL = this.createForm.photoURL.trim() || null;
      newContact.Status = this.createForm.status;

      await newContact.Save();
      await this.loadData();
      this.isCreating = false;
      this.showSuccess('Contact created');
    } catch (error) {
      console.error('Error creating contact:', error);
      this.errorMessage = 'Failed to create contact.';
    } finally {
      this.isSaving = false;
    }
  }

  startEdit(display: ContactDisplay): void {
    display.isEditing = true;
    display.editForm = this.contactToForm(display.contact);
  }

  cancelEdit(display: ContactDisplay): void {
    display.isEditing = false;
  }

  async saveEdit(display: ContactDisplay): Promise<void> {
    this.isSaving = true;
    try {
      display.contact.FirstName = display.editForm.firstName.trim();
      display.contact.LastName = display.editForm.lastName.trim();
      display.contact.Email = display.editForm.email.trim();
      display.contact.Title = display.editForm.title.trim() || null;
      display.contact.PhotoURL = display.editForm.photoURL.trim() || null;
      display.contact.Status = display.editForm.status;

      await display.contact.Save();
      display.isEditing = false;
      this.showSuccess('Contact updated');
    } catch (error) {
      console.error('Error updating contact:', error);
      this.errorMessage = 'Failed to update contact.';
    } finally {
      this.isSaving = false;
    }
  }

  async confirmDelete(display: ContactDisplay): Promise<void> {
    // Pre-check: a Person can't be deleted while ANY OrganizationPersonRole
    // rows reference them (FK_OrganizationPersonRole_Person). That FK doesn't
    // care about Relationship.Status — even ended memberships keep their OPR
    // row, which blocks the delete. Count both active and ended so the user
    // gets an honest message; phrase it generically.
    let activeCount = 0;
    let totalCount = 0;
    try {
      const rv = new RunView();
      const result = await rv.RunView<{ Status: string | null }>({
        EntityName: 'BC: Organization Person Roles',
        ExtraFilter: `PersonID = '${display.contact.ID}'`,
        ResultType: 'simple'
      });
      const rows = result.Success ? (result.Results ?? []) : [];
      totalCount = rows.length;
      activeCount = rows.filter(r => r.Status === 'Active').length;
    } catch (error) {
      console.error('Error checking memberships before delete:', error);
      this.errorMessage = 'Could not verify contact memberships. Try again.';
      return;
    }

    if (totalCount > 0) {
      const name = `${display.contact.FirstName} ${display.contact.LastName}`.trim() || display.contact.Email;
      if (activeCount > 0) {
        const label = activeCount === 1 ? 'membership' : 'memberships';
        this.errorMessage =
          `Can't delete ${name} — they have ${activeCount} active organization ${label}. ` +
          `Remove the contact from their organizations first (Members tab), then try again.`;
      } else {
        // Only inactive/ended memberships exist, but the OPR rows still hold the FK.
        this.errorMessage =
          `Can't delete ${name} — they have ${totalCount} historical organization ` +
          `membership${totalCount === 1 ? '' : 's'} still referencing this contact. ` +
          `Contact engineering — those rows need to be cleaned up server-side before this ` +
          `contact can be deleted.`;
      }
      return;
    }

    this.showConfirmDialog(
      'Delete Contact',
      `Are you sure you want to delete ${display.contact.FirstName} ${display.contact.LastName}?`,
      async () => {
        this.isSaving = true;
        try {
          await display.contact.Delete();
          await this.loadData();
          this.showSuccess('Contact deleted');
        } catch (error) {
          console.error('Error deleting contact:', error);
          this.errorMessage = 'Failed to delete contact.';
        } finally {
          this.isSaving = false;
        }
      }
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active': return 'status-active';
      case 'Pending': return 'status-pending';
      case 'Inactive': return 'status-inactive';
      case 'Suspended': return 'status-revoked';
      default: return '';
    }
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
  }

  protected showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadContactsComponent(): void {}
